import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchedOnce = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me.php');
      if (data.success) setUser(data.data);
      else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run exactly once on mount — ref guard prevents StrictMode double-invoke from looping
  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (username, password) => {
    const { data } = await api.post('/auth/login.php', { username, password });
    if (data.success) {
      await fetchMe();
      return data;
    }
    throw new Error(data.error || 'Login failed');
  }, [fetchMe]);

  const logout = useCallback(async () => {
    await api.post('/auth/logout.php');
    setUser(null);
  }, []);

  // Memoize context value so consumers don't re-render (and re-trigger effects) every render
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    refreshUser: fetchMe,
  }), [user, loading, login, logout, fetchMe]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
